const INSTALL_DISMISS_KEY = 'yocas-pwa-install-dismissed-at';
const INSTALL_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * @returns {boolean}
 */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  const hasMatchMedia = typeof window.matchMedia === 'function';
  return (
    (hasMatchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    /** @type {Window & { navigator: { standalone?: boolean } }} */ (window).navigator
      .standalone === true
  );
}

/**
 * @returns {boolean}
 */
export function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767px)').matches;
}

/**
 * @returns {'ipad' | 'iphone' | 'android' | 'samsung' | 'other'}
 */
export function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;

  if (/iPad|Macintosh/.test(ua) && 'ontouchend' in document) return 'ipad';
  if (/iPhone|iPod/.test(ua)) return 'iphone';
  if (/SamsungBrowser|SM-T|SM-X|Galaxy Tab/i.test(ua)) return 'samsung';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * @returns {boolean}
 */
export function isIosPhone() {
  return detectPlatform() === 'iphone';
}

/**
 * @returns {boolean}
 */
export function isPhoneDevice() {
  if (typeof navigator === 'undefined') return false;
  const platform = detectPlatform();

  // iPhone stays a phone in landscape (viewport can exceed 767px).
  if (platform === 'iphone' || platform === 'iPod') return true;

  if (platform === 'android' || platform === 'samsung') {
    return isMobileViewport();
  }

  return false;
}

/**
 * Screen Orientation API works on Android PWAs, not on iOS Safari/PWA.
 * @returns {boolean}
 */
export function supportsOrientationLock() {
  return isPhoneDevice() && !isIosDevice();
}

/**
 * Element Fullscreen API works on Android/Chrome, not on iOS for arbitrary divs.
 * @returns {boolean}
 */
export function supportsElementFullscreen() {
  return isPhoneDevice() && !isIosDevice();
}

/**
 * @returns {boolean}
 */
export function isIosDevice() {
  const platform = detectPlatform();
  return platform === 'ipad' || platform === 'iphone';
}

/**
 * @returns {boolean}
 */
export function shouldShowInstallGate() {
  if (isStandalone()) return false;
  const platform = detectPlatform();
  return platform === 'ipad' || platform === 'samsung';
}

/**
 * @returns {boolean}
 */
export function wasInstallPromptDismissedRecently() {
  if (typeof localStorage === 'undefined') return false;

  const dismissedAt = Number(localStorage.getItem(INSTALL_DISMISS_KEY));
  if (!Number.isFinite(dismissedAt)) return false;

  return Date.now() - dismissedAt < INSTALL_DISMISS_MS;
}

export function dismissInstallPrompt() {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()));
}

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function shouldShowMobileInstallPrompt(pathname) {
  if (!isMobileViewport() || isStandalone()) return false;
  if (wasInstallPromptDismissedRecently()) return false;

  const excluded = ['/intro', '/install', '/welcome', '/login'];
  return !excluded.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}
